import React, { useMemo } from 'react';
import { Link, useHistory, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useIntl } from 'react-intl';
import { Select } from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowUpRightFromSquare, faCopy, faStore } from '@fortawesome/free-solid-svg-icons';

import { ILoginState, IMenuState, IMyProfileState } from '@share/store/slices';
import { Routes } from '@share/constants';
import { FunctionalRootState, getAccountUsernameFromPath, Toaster } from '@share/utils';

import { MySideKickRewards } from '../sidekicks';

import { AccountBookings } from './components/bookings';
import { AccountCertificates } from './components/certificates';
import { AccountAgentTool } from './components/agent-tools';
import { AccountReports } from './components/reports';
import { AccountOverview } from './components/overview';
import { AccountQuotes } from './components/quotes';
import { AccountTravelHistory } from './components/travel-history';
import { AccountSavedProperties } from './components/saved-properties';
import { SECTION_ICONS } from './icons';
import { AccountPasswordForm, AccountProfileSection } from './components/profile-section';
import type { AccountSectionId, IAccountSection } from './types';
import {
  getAccountSectionIdFromPathname,
  getAccountSectionPath,
  getAccountSections,
  getHubThemeStyle,
  groupAccountSections,
  isAccountTravelAgent,
} from './utils';

import styles from './styles.module.scss';

const SECTION_CONTENT: Record<Exclude<AccountSectionId, 'overview'>, (accountUsername: string) => React.ReactElement> = {
  bookings: () => <AccountBookings />,
  history: () => <AccountTravelHistory />,
  quotes: () => <AccountQuotes />,
  saved: (accountUsername) => <AccountSavedProperties accountUsername={accountUsername} />,
  certificates: (accountUsername) => <AccountCertificates accountUsername={accountUsername} />,
  reports: () => <AccountReports />,
  website: () => <AccountAgentTool tool="website" />,
  manualUpload: () => <AccountAgentTool tool="manualUpload" />,
  supplier: () => <AccountAgentTool tool="supplier" />,
  profile: () => <AccountProfileSection part="profile" />,
  authorized: () => <AccountProfileSection part="authorized" />,
  payment: () => <AccountProfileSection part="payment" />,
  password: () => <AccountPasswordForm />,
  sidekick: () => <MySideKickRewards />,
};

export const AccountHub = (): React.ReactElement | null => {
  const intl = useIntl();
  const history = useHistory();
  const location = useLocation();

  const loginStore: ILoginState = useSelector((state: FunctionalRootState) => state.loginStore);
  const menuStore: IMenuState = useSelector((state: FunctionalRootState) => state.navigationMenuStore);
  const myProfileStore: IMyProfileState = useSelector((state: FunctionalRootState) => state.myProfileStore);

  const { account, user } = loginStore;
  const items = menuStore?.items;

  const sections = useMemo(() => getAccountSections(account, items), [account, items]);
  const groups = useMemo(() => groupAccountSections(sections), [sections]);

  const accountUsername = getAccountUsernameFromPath(history);
  const requestedId = getAccountSectionIdFromPathname(location.pathname);
  const active: IAccountSection | undefined = sections.find(section => section.id === requestedId) || sections[0];

  if (!active) {
    return null;
  }

  const getLabel = (section: IAccountSection): string => section.label || intl.formatMessage({ id: section.labelId });
  const getGroupLabel = (labelId: string | null): string => (labelId ? intl.formatMessage({ id: labelId }) : '');

  const firstName = myProfileStore?.result?.profile?.firstName || user?.firstName;
  const memberSince = myProfileStore?.result?.profile?.memberSince;
  const isOverview = active.id === 'overview';

  // Travel agents reach the hub from "My Dashboard", so their Overview keeps that name.
  const overviewTitleId = isAccountTravelAgent(account, items) ? 'my.dashboard' : 'my.account.title';
  const title = isOverview ? intl.formatMessage({ id: overviewTitleId }) : getLabel(active);
  const subtitle = isOverview && firstName
    ? intl.formatMessage(
      { id: memberSince ? 'my.account.hub.welcome_since' : 'my.account.hub.welcome' },
      { name: firstName, date: memberSince },
    )
    : null;

  const themeStyle = getHubThemeStyle(account);

  // Same ID the Overview's balance card shows: the menu's RSI ID, else the user's key ID.
  const memberId = String(items?.rsiId || user?.keyid || '');
  const handleCopyMemberId = () => {
    if (!navigator.clipboard) {
      Toaster.error(intl.formatMessage({ id: 'my.account.hub.member_id_copy_failed' }));
      return;
    }
    navigator.clipboard.writeText(memberId)
      .then(() => Toaster.success(intl.formatMessage({ id: 'my.account.hub.member_id_copied' })))
      .catch((error: unknown) => {
        console.error('Account hub: copying the member ID failed', error);
        Toaster.error(intl.formatMessage({ id: 'my.account.hub.member_id_copy_failed' }));
      });
  };

  // The certificate catalog lives outside the hub (its own browse and purchase flow); the rail links to it.
  const catalogPath = sections.some(section => section.id === 'certificates')
    ? `/${accountUsername}${Routes.CertificatesCatalog}`
    : null;

  const handlePickerChange = (id: AccountSectionId | 'catalog') => {
    if (id === 'catalog' && catalogPath) {
      history.push(catalogPath);
      return;
    }
    const section = sections.find(item => item.id === id);
    if (section) {
      history.push(getAccountSectionPath(accountUsername, section));
    }
  };

  return (
    <div className={styles.hub} style={themeStyle}>
      <div className={styles.container}>
        <header className={styles.pageHeader}>
          <div className={styles.pageHeaderText}>
            <h1 className={styles.title}>{title}</h1>
            {subtitle ? <p className={styles.subtitle}>{subtitle}</p> : null}
          </div>
          {/* The member ID is what support and checkout ask for, so it's always on screen here. */}
          {memberId ? (
            <div className={styles.memberId}>
              <span className={styles.memberIdLabel}>{intl.formatMessage({ id: 'my.account.hub.member_id_label' })}</span>
              <span className={styles.memberIdShort} aria-hidden="true">{intl.formatMessage({ id: 'id' })}</span>
              <span className={styles.memberIdValue}>{memberId}</span>
              <button
                type="button"
                className={styles.memberIdCopy}
                onClick={handleCopyMemberId}
                aria-label={intl.formatMessage({ id: 'my.account.hub.member_id_copy' }, { id: memberId })}
                title={intl.formatMessage({ id: 'my.account.hub.member_id_copy' }, { id: memberId })}
              >
                <FontAwesomeIcon icon={faCopy} aria-hidden="true" />
              </button>
            </div>
          ) : null}
        </header>

        <div className={styles.layout}>
          <nav className={styles.rail} aria-label={intl.formatMessage({ id: 'my.account.title' })}>
            {groups.map(group => (
              <div key={group.id || 'root'} className={styles.railGroup}>
                {group.labelId ? <div className={styles.railGroupLabel}>{getGroupLabel(group.labelId)}</div> : null}
                {group.sections.map(section => {
                  const isActive = section.id === active.id;
                  return (
                    <Link
                      key={section.id}
                      to={getAccountSectionPath(accountUsername, section)}
                      className={isActive ? `${styles.railItem} ${styles.railItemActive}` : styles.railItem}
                      aria-current={isActive ? 'page' : undefined}
                    >
                      <FontAwesomeIcon icon={SECTION_ICONS[section.id]} className={styles.railIcon} aria-hidden="true" />
                      {getLabel(section)}
                    </Link>
                  );
                })}
                {group.id === 'certificates' && catalogPath ? (
                  <Link to={catalogPath} className={styles.railItem}>
                    <FontAwesomeIcon icon={faStore} className={styles.railIcon} aria-hidden="true" />
                    {intl.formatMessage({ id: 'my.account.hub.certificates.browse_catalog' })}
                    <FontAwesomeIcon icon={faArrowUpRightFromSquare} className={styles.railExternal} aria-hidden="true" />
                  </Link>
                ) : null}
              </div>
            ))}
          </nav>

          <div className={styles.picker}>
            <Select<AccountSectionId | 'catalog'>
              value={active.id}
              onChange={handlePickerChange}
              className={styles.pickerSelect}
              aria-label={intl.formatMessage({ id: 'my.account.hub.section_picker' })}
              options={[
                ...sections.map(section => ({
                  value: section.id as AccountSectionId | 'catalog',
                  label: section.group
                    ? `${getGroupLabel(groups.find(group => group.id === section.group)?.labelId || null)} · ${getLabel(section)}`
                    : getLabel(section),
                })),
                ...(catalogPath ? [{
                  value: 'catalog' as const,
                  label: `${getGroupLabel('my.account.hub.group.certificates')} · ${intl.formatMessage({ id: 'my.account.hub.certificates.browse_catalog' })}`,
                }] : []),
              ]}
            />
          </div>

          <main className={styles.content}>
            {isOverview
              ? <AccountOverview sections={sections} accountUsername={accountUsername} />
              : SECTION_CONTENT[active.id as Exclude<AccountSectionId, 'overview'>](accountUsername)}
          </main>
        </div>
      </div>
    </div>
  );
};
